import React, {useState, useEffect, useCallback, useRef} from "react";
import "./Search.css";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import ListGroup from "react-bootstrap/ListGroup";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import LoadingIcon from "../../Partials/LoadingIcon/LoadingIcon";
import HomeNameBadge from "../../Partials/HomeNameBadge/index.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircle, faIdCard, faHouse} from "@fortawesome/free-solid-svg-icons";
import {faApple, faAndroid} from "@fortawesome/free-brands-svg-icons";
import { useTimezone } from "../../../contexts/TimezoneContext";
import { truncateFirmwareVersion, formatTargetName } from "../../../../../lib/ui/badgeUtils";
import { getBondColor } from "../../../../../lib/ui/colorUtils";
import { useAuth } from "../../../../../contexts/AuthContext";
import { searchUsers, searchBonds } from "../../../libs/dashboard";

const animations = [
    "fa-bounce",
    "fa-shake",
    "fa-flip",
    "fa-beat",
    "fa-beat-fade",
    "fa-fade",
    "animate",
]

function Search({
                    initialSearchType = "users", onResultClick
                }) {
    const { formatDateCustom } = useTimezone();
    const { getToken } = useAuth();
    const [searchType, setSearchType] = useState(initialSearchType);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [error, setError] = useState(null);
    const [savedQueries, setSavedQueries] = useState([]);
    const skipDebounceRef = useRef(false);
    const inputRef = useRef(null);

    // Load saved queries from localStorage on mount
    useEffect(() => {
        const saved = localStorage.getItem('searchQueries');
        if (saved) {
            try {
                setSavedQueries(JSON.parse(saved));
            } catch (e) {
                console.error('Error loading saved queries:', e);
            }
        }
    }, []);

    // Auto-focus input on mount
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const performSearch = useCallback(async (searchQuery) => {
        setIsLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            const token = getToken();
            let data;

            if (searchType === "users") {
                data = await searchUsers(token, searchQuery);
            } else if (searchType === "bonds") {
                data = await searchBonds(token, searchQuery);
            }

            setResults(data.results || []);
        } catch (err) {
            console.error('Search error:', err);
            setError(err.message);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchType, getToken]);

    // Debounced search
    useEffect(() => {
        if (!query || query.length < 2) {
            setResults([]);
            setHasSearched(false);
            return;
        }

        // If skipDebounce flag is set, search immediately
        if (skipDebounceRef.current) {
            skipDebounceRef.current = false;
            performSearch(query);
            return;
        }

        // Otherwise, debounce the search
        const timeoutId = setTimeout(() => {
            performSearch(query);
        }, 700);

        return () => clearTimeout(timeoutId);
    }, [query, performSearch]);

    const handleResultClick = (result) => {
        // Save query to localStorage
        if (query && query.trim()) {
            const newQueries = [query.trim(), ...savedQueries.filter(q => q !== query.trim())].slice(0, 10);
            setSavedQueries(newQueries);
            localStorage.setItem('searchQueries', JSON.stringify(newQueries));
        }

        if (onResultClick) {
            onResultClick(result);
        }
    };

    const removeQuery = (queryToRemove) => {
        const newQueries = savedQueries.filter(q => q !== queryToRemove);
        setSavedQueries(newQueries);
        localStorage.setItem('searchQueries', JSON.stringify(newQueries));
    };

    const handleQueryBadgeClick = (savedQuery) => {
        setQuery(savedQuery);
        skipDebounceRef.current = true;
    };

    const handleSearchTypeChange = (newType) => {
        setSearchType(newType);
        setResults([]);
        setError(null);

        // Set flag to skip debounce on next search (if there's a query)
        if (query && query.length >= 2) {
            skipDebounceRef.current = true;
        } else {
            setHasSearched(false);
        }
    };

    const getMobileInfo = (mobile) => {
        const isApple = mobile.manufacturer.toLowerCase() === "apple";
        return {
            icon: isApple ? faApple : faAndroid,
            color: isApple ? "primary" : "success"
        };
    };

    const renderUserResult = (user) => {
        const lastMobiles = user.mobiles ? user.mobiles.slice(-2) : [];
        const enabledIntegrations = user.integrations
            ? user.integrations.filter(i => i.is_active === "True")
            : [];

        return (
            <ListGroup.Item
                key={user.id}
                action
                onClick={() => handleResultClick(user)}
                className="search-result-item"
            >
                {/* Line 1: icon + full name + email badge */}
                <div className="d-flex align-items-center gap-2 mb-1">
                    <FontAwesomeIcon icon={faIdCard} style={{fontSize: '1.15rem', flexShrink: 0}} />
                    <span style={{fontSize: '1.15rem', fontWeight: 600}}>{user.name}</span>
                    <Badge bg="secondary" className="fw-light" style={{fontSize: '0.8rem'}}>{user.email}</Badge>
                </div>

                {/* Line 2: joined (left) + account_code + user_id (right) */}
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>
                        {user.created && (
                            <Badge bg="info" className="fw-light" style={{fontSize: '0.75rem'}}>
                                Joined {formatDateCustom(user.created, 'date')}
                            </Badge>
                        )}
                    </div>
                    <div className="d-flex gap-1">
                        {user.account_code && (
                            <Badge bg="secondary" style={{fontSize: '0.75rem'}}>{user.account_code}</Badge>
                        )}
                        <Badge bg="dark" style={{fontSize: '0.75rem'}}>{user.id}</Badge>
                    </div>
                </div>

                {/* Line 3: last 2 mobiles (left) + enabled integrations (right) */}
                {(lastMobiles.length > 0 || enabledIntegrations.length > 0) && (
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <div className="d-flex gap-1 flex-wrap">
                            {lastMobiles.map((mobile, idx) => {
                                const mobileInfo = getMobileInfo(mobile);
                                return (
                                    <Badge key={idx} bg={mobileInfo.color} style={{fontSize: '0.75rem'}}>
                                        <FontAwesomeIcon icon={mobileInfo.icon} className="me-1" />
                                        {mobile.manufacturer}
                                    </Badge>
                                );
                            })}
                        </div>
                        <div className="d-flex gap-1 flex-wrap justify-content-end">
                            {enabledIntegrations.map((integration, idx) => (
                                <Badge key={idx} bg="success" style={{fontSize: '0.75rem'}}>
                                    {integration.integration_client_id}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bond IDs */}
                {user.bonds && user.bonds.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                        {user.bonds.map((bond, idx) => (
                            <Badge
                                key={idx}
                                className="bondBadge"
                                style={{'--bond-badge-bg': getBondColor(bond.bond_id), fontSize: '0.9rem'}}
                            >
                                {bond.bond_id}
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Homes: <name> - <role> - <bond ids> */}
                {user.homes && user.homes.length > 0 && (
                    <div className="mt-2">
                        <div className="text-muted fw-bold mb-1" style={{fontSize: '0.75rem'}}>
                            <FontAwesomeIcon icon={faHouse} className="me-1" />Homes
                        </div>
                        {user.homes.map((home, idx) => (
                            <div key={home.home_id || idx} className="d-flex align-items-center justify-content-between gap-2 mb-1">
                                <div className="d-flex align-items-center gap-2" style={{minWidth: 0}}>
                                    <Badge
                                        bg={home.role === 'owner' ? 'success' : home.role === 'dealer' ? 'warning' : 'secondary'}
                                        style={{fontSize: '0.7rem'}}
                                    >
                                        {home.role}
                                    </Badge>
                                    <HomeNameBadge name={home.name} style={{fontSize: '0.8rem'}} />
                                </div>
                                <div className="d-flex flex-wrap gap-1 justify-content-end">
                                    {(home.bond_ids || []).map((bondId, i) => (
                                        <Badge
                                            key={i}
                                            className="bondBadge"
                                            style={{'--bond-badge-bg': getBondColor(bondId), fontSize: '0.8rem'}}
                                        >
                                            {bondId}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </ListGroup.Item>
        );
    };

    const renderBondResult = (bond) => {
        const sysVersion = bond.sys?.version || {};
        const bridge = bond.bridge || {};
        const owner = bond.owner_info || {};
        const connectivity = bond.connectivity || {};

        return (<ListGroup.Item
            key={bond.id}
            action
            onClick={() => handleResultClick(bond)}
            className="search-result-item"
        >
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <h6 className="mb-1 fw-bold">
                        <FontAwesomeIcon
                            icon={faCircle}
                            className="me-2"
                            style={{color: connectivity.connected ? '#4caf7d' : '#e57373'}}
                        />
                        {bond.id}
                        {connectivity.connected !== undefined && (
                            <Badge bg={connectivity.connected ? "success" : "danger"} className="ms-2">
                                {connectivity.connected ? "Online" : "Offline"}
                            </Badge>
                        )}
                        {sysVersion.target && (<Badge bg="info" className="ms-2 fw-light">
                            {formatTargetName(sysVersion.target)}
                        </Badge>)}
                    </h6>

                    {bridge.name && (<div className="mt-1">
                        <strong>{bridge.name}</strong>
                        {bridge.location && <span className="text-muted"> - {bridge.location}</span>}
                    </div>)}

                    {sysVersion.make && (<div className="mt-1">
                        <Badge bg="secondary" className="me-1 small">
                            {sysVersion.make} {sysVersion.model}
                        </Badge>
                        {sysVersion.fw_ver && (<Badge bg="secondary" className="me-1 small" title={sysVersion.fw_ver}>
                            {truncateFirmwareVersion(sysVersion.fw_ver)}
                        </Badge>)}
                    </div>)}


                    {owner.name && (<div className="mt-2">
                        <h6 className="text-muted fw-bold"><FontAwesomeIcon icon={faIdCard}/> {owner.name}</h6>
                        <Badge bg="secondary" className="me-1">{owner.email}
                        </Badge>
                    </div>)}

                    {owner.integrations && owner.integrations.length > 0 && (<div className="mt-1">
                        <div className="d-flex flex-wrap gap-1">
                            {owner.integrations.map((integration, idx) => (<Badge
                                key={idx}
                                bg={integration.is_active === "True" ? "success" : "danger"}
                                className="me-1 mb-1"
                            >
                                {integration.integration_client_id}
                                {integration.is_active === "True" && " ✓"}
                            </Badge>))}
                        </div>
                    </div>)}

                    <div className="mt-1">
                        {connectivity.timestamp && (
                            <small className="text-muted d-block">
                                Last Seen: {formatDateCustom(new Date(connectivity.timestamp).toISOString(), 'datetime')}
                            </small>
                        )}
                        <small className="text-muted d-block">
                            Joined: {formatDateCustom(bond.created, 'datetime')}
                        </small>
                        <small className="text-muted d-block">
                            Updated: {formatDateCustom(bond.modified, 'datetime')}
                        </small>
                    </div>
                </div>
            </div>
        </ListGroup.Item>);
    };


    return (<div className="search-component">
        <InputGroup className="mb-3">
            <Form.Control
                ref={inputRef}
                type="text"
                placeholder={"Search..."}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="search-input"
            />
            <Button
                variant={searchType === "bonds" ? "primary" : "outline-secondary"}
                onClick={() => handleSearchTypeChange("bonds")}
                size="sm"
                className="search-toggle-btn"
            >
                Bridges
            </Button>
            <Button
                variant={searchType === "users" ? "primary" : "outline-secondary"}
                onClick={() => handleSearchTypeChange("users")}
                size="sm"
                className="search-toggle-btn"
            >
                Users
            </Button>
        </InputGroup>

        {savedQueries.length > 0 && !query && (
            <div className="mb-3 d-flex flex-wrap gap-2">
                {savedQueries.map((savedQuery, idx) => (
                    <Badge
                        key={idx}
                        bg="secondary"
                        className="d-flex align-items-center"
                        style={{cursor: 'pointer', gap: '0.5rem'}}
                    >
                        <span onClick={() => handleQueryBadgeClick(savedQuery)}>
                            {savedQuery}
                        </span>
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                removeQuery(savedQuery);
                            }}
                            style={{cursor: 'pointer', fontWeight: 'bold'}}
                        >
                            ×
                        </span>
                    </Badge>
                ))}
            </div>
        )}

        {isLoading && (<div className="text-center my-3">
            <LoadingIcon animation={animations[Math.floor(Math.random() * animations.length)]}/>
        </div>)}

        {error && (<div className="alert alert-danger small" role="alert">
            Error: {error}
        </div>)}

        {!isLoading && hasSearched && results.length === 0 && (<div className="text-center my-3">
            <p className="text-muted small">No results found for "{query}"</p>
        </div>)}

        {!isLoading && results.length > 0 && (<div className="search-results">
            <ListGroup className="search-results-list">
                {results.map((result) => {
                    switch (searchType) {
                        case "users":
                            return renderUserResult(result);
                        case "bonds":
                            return renderBondResult(result);
                        default:
                            return null;
                    }
                })}
            </ListGroup>
        </div>)}
    </div>);
}

export default Search;
